import {
  FolderOpen,
  Construction,
  FileSearch,
  FileText,
  Image as ImageIcon,
  Bot,
  Database,
} from 'lucide-react';

/**
 * Page DOMAIN — Documents (placeholder + vision lecture sémantique)
 *
 * Centralise tous les documents apprenant Drive (vieux 2023 + récents
 * structurés + docs ad hoc déposés en cours de formation comme arrêts
 * maladie, emails clients, etc.).
 *
 * VISION CIBLE (Rafik 28/05/2026) :
 *   "on doit avoir la capacité de lire le contenu du dossier lui-même, les
 *    fichiers contenus à l'intérieur, et identifier ce que c'est. Avoir une
 *    sémantique et une lecture du document en question. C'est ce qui va
 *    nous faire gagner énormément de temps pour la suite."
 *
 * → Service `documents-classifier` (cf. apps/api/src/services/documents-classifier/)
 *   qui scanne chaque fichier d'un dossier apprenant, en extrait le texte
 *   (PDF natif via pdf-parse, image scan via OCR Google Vision, Google Doc
 *   via API Docs, DOCX via mammoth), puis le classifie sémantiquement via
 *   LLM Claude → Convention / Attestation / Arrêt maladie / Email élève /
 *   etc. Indexé en Postgres pour recherche full-text + tableau de bord.
 *
 * STATUS : SQUELETTE — endpoint POST /v2/apprenants/:id/documents/scan
 * renvoie 501 not_implemented pour l'instant. À brancher quand le validate
 * sera rodé.
 */
export function DocumentsPage() {
  return (
    <div className="mx-auto max-w-7xl space-y-5">
      {/* Header */}
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
          Domaine — Centralisation
        </div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-neutral-900">
          <FolderOpen className="text-primary-600 h-6 w-6" />
          Documents
        </h1>
        <p className="mt-1 text-sm text-neutral-600">
          Source de vérité pour tous les fichiers déposés dans les dossiers Drive apprenants —
          documents officiels, courriers reçus, justificatifs ad hoc. Lecture sémantique +
          classification auto à venir.
        </p>
      </div>

      {/* Bandeau en construction */}
      <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
        <Construction className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-amber-900">
            Squelette posé — implémentation différée
          </div>
          <p className="mt-1 text-xs text-amber-800">
            La structure backend est prête (service{' '}
            <code className="rounded bg-amber-100 px-1 py-0.5">documents-classifier</code> +
            endpoint{' '}
            <code className="rounded bg-amber-100 px-1 py-0.5">
              POST /v2/apprenants/:id/documents/scan
            </code>
            ). Les extracteurs PDF/OCR/GDoc et le classifier LLM seront branchés une fois le flow
            validate rodé (Rafik 28/05/2026).
          </p>
        </div>
      </div>

      {/* Vision en 4 étapes */}
      <div className="rounded-xl border border-neutral-200 bg-white p-5">
        <div className="text-primary-700 mb-4 text-[10px] font-bold uppercase tracking-widest">
          🎯 Pipeline cible : Drive → Sens
        </div>
        <div className="grid gap-4 md:grid-cols-4">
          <Step
            n={1}
            icon={<FileSearch className="h-5 w-5 text-sky-600" />}
            title="Scan"
            desc="Liste tous les fichiers du dossier apprenant Drive (vieux 2023 + récents)."
          />
          <Step
            n={2}
            icon={<FileText className="h-5 w-5 text-violet-600" />}
            title="Extraction"
            desc="Texte : pdf-parse (PDF natif), Google Vision OCR (scans/images), API Docs (GDoc), mammoth (DOCX)."
          />
          <Step
            n={3}
            icon={<Bot className="h-5 w-5 text-emerald-600" />}
            title="Classification"
            desc="LLM Claude classifie le texte → Convention / Attestation / Arrêt maladie / Email élève / Pôle Emploi / OPCO / ..."
          />
          <Step
            n={4}
            icon={<Database className="h-5 w-5 text-amber-600" />}
            title="Indexation"
            desc="Postgres ApprenantDocumentClassification + tsvector full-text search."
          />
        </div>
      </div>

      {/* Catégories sémantiques cibles */}
      <div className="rounded-xl border border-neutral-200 bg-white p-5">
        <div className="text-primary-700 mb-3 text-[10px] font-bold uppercase tracking-widest">
          🏷️ Catégories de classification
        </div>
        <div className="flex flex-wrap gap-2 text-[11px]">
          {[
            'Convention',
            'Attestation',
            'Certificat',
            'Émargement',
            'Arrêt maladie',
            'Email élève',
            'Devis',
            'Facture',
            'Convocation',
            'Bilan pédagogique',
            'Rapport progression',
            'Auto-financement',
            'Pôle Emploi',
            'OPCO',
            'Autre',
          ].map((c) => (
            <span
              key={c}
              className="rounded-full border border-neutral-200 bg-neutral-50 px-2 py-1 font-medium text-neutral-700"
            >
              {c}
            </span>
          ))}
        </div>
      </div>

      {/* Bénéfices attendus */}
      <div className="rounded-xl border border-neutral-200 bg-white p-5 text-xs text-neutral-700">
        <div className="text-primary-700 mb-2 text-[10px] font-bold uppercase tracking-widest">
          💡 Gains attendus
        </div>
        <ul className="space-y-1 pl-4">
          <li className="list-disc">
            <strong>Migration auto</strong> des vieux dossiers 2023 dont la structuration n'était
            pas la même qu'aujourd'hui.
          </li>
          <li className="list-disc">
            <strong>Détection arrêts maladie</strong> → ajustement automatique du % d'assiduité
            Qualiopi.
          </li>
          <li className="list-disc">
            <strong>Audit Qualiopi instantané</strong> : "Manque-t-il un certificat ? Une convention
            signée ?"
          </li>
          <li className="list-disc">
            <strong>Recherche full-text</strong> : "Trouve tous les apprenants ayant déposé un arrêt
            maladie en 2025".
          </li>
        </ul>
      </div>

      {/* Note méthodo */}
      <div className="flex items-center gap-2 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-[11px] text-neutral-600">
        <ImageIcon className="h-4 w-4 text-neutral-400" />
        Les fichiers déposés manuellement dans le dossier apprenant restent dans Drive — le
        classifier les <em>lit</em> et les <em>indexe</em>, il ne les déplace pas.
      </div>
    </div>
  );
}

function Step({
  n,
  icon,
  title,
  desc,
}: {
  n: number;
  icon: React.ReactNode;
  title: string;
  desc: string;
}) {
  return (
    <div className="relative rounded-lg border border-neutral-200 bg-neutral-50/50 p-3">
      <div className="text-primary-300 absolute right-2 top-1 text-3xl font-black">{n}</div>
      <div className="mb-1.5 flex items-center gap-2">
        {icon}
        <div className="text-sm font-bold text-neutral-900">{title}</div>
      </div>
      <p className="text-[11px] text-neutral-600">{desc}</p>
    </div>
  );
}
